import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { FilterList, FilterModel, FilterModelStoreFront } from '@app/shared/models/product';
import { FormGroup } from '@angular/forms';
import { LocalStorageService } from '../../../core/services/localStorage/local-storage.service';
import { DecorationTypeService } from '@app/shared/services/decoration-type.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.css']
})
export class FilterComponent implements OnInit, OnDestroy {

  /** Decoration types loaded from the lookup endpoint at runtime. */
  decorationTypes: { DecorationTypeId: number; Name: string; Code?: string }[] = [];

  /**
   * Generic selected set — keyed by Name (lower-case for case-insensitive match).
   * Default: all types selected (mirrors old behaviour where both booleans were true).
   * When the lookup returns 0 items (e.g. network error) we keep it empty and the
   * backward-compat getters fall back to returning true so the consumer still works.
   */
  selectedDecorationNames: Set<string> = new Set<string>();

  private _decorationSub: Subscription | null = null;

  /** localStorage key for the generic decoration selection. */
  private readonly DECORATION_FILTER_KEY = 'decorationFilter';

  @Input() filterModel!: FilterModelStoreFront | FilterModel;
  @Input() filterList!: FilterList;
  @Input() pageSize = 10; // No of items to load from server side i.e no of items to take
  @Input() searchCustomizedForm!: FormGroup;
  @Output() getProducts = new EventEmitter <any>();
  isDisableFilterBtn = true;
  //selectedCategory: any[] = [];
  selectedBrand: any[] = [];
  selectedSize: any[] = [];
  selectedColor: any[] = [];
  selectedPrintLocation: any[] = [];
  flag: Boolean;
  showCategories: string;
  showBrand: string;
  showSize: string;
  showColor: string
  showPrintLocation: string;

  searchBrand: string;
  searchSize: string;
  searchColor: string;
  searchPrintLocation: string;
  // Material paginator fields
  @ViewChildren('brandCheckbox') private brandCheckboxes!: QueryList<any>;
  @ViewChildren('colorCheckbox') private colorCheckboxes!: QueryList<any>;
  @ViewChildren('sizeCheckbox') private sizeCheckboxes!: QueryList<any>;
  @ViewChildren('printLocationCheckbox') private printLocationCheckboxes!: QueryList<any>;
  constructor(
    private localstorageService: LocalStorageService,
    private decorationTypeService: DecorationTypeService
  ) { }

  ngOnInit() {

    this.selectedBrand = [];
    this.selectedSize = [];
    this.selectedColor = [];
    this.selectedPrintLocation = [];
    this.flag = false;
    this.showCategories = "";
    this.showBrand = "";
    this.showSize = "";
    this.showColor = "";
    this.showPrintLocation = "";
    if (localStorage.getItem("ExistingfilterdData") == "true") {

      localStorage.setItem("ExistingfilterdData", "false")
      this.showCategories = localStorage.getItem("showCategories") == undefined ? "" : localStorage.getItem("showCategories");
      this.showBrand = localStorage.getItem("showBrand") == undefined ? "" : localStorage.getItem("showBrand");
      this.showSize = localStorage.getItem("showSize") == undefined ? "" : localStorage.getItem("showSize");
      this.showColor = localStorage.getItem("showColor") == undefined ? "" : localStorage.getItem("showColor");
      this.showPrintLocation = localStorage.getItem("showPrintLocation") == undefined ? "" : localStorage.getItem("showPrintLocation");
      localStorage.removeItem("showCategories");
      localStorage.removeItem("showBrand");
      localStorage.removeItem("showSize");
      localStorage.removeItem("showColor");
      localStorage.removeItem("showPrintLocation");

    }

    // Load decoration types from the lookup endpoint and restore persisted selection.
    this._decorationSub = this.decorationTypeService.getDecorationTypes().subscribe({
      next: (types) => {
        this.decorationTypes = types;

        // Restore saved selection from localStorage (stored as JSON array of lower-cased names).
        const saved = localStorage.getItem(this.DECORATION_FILTER_KEY);
        if (saved) {
          try {
            const parsed: string[] = JSON.parse(saved);
            this.selectedDecorationNames = new Set<string>(parsed);
          } catch {
            this.selectedDecorationNames = this._allNamesSet();
          }
        } else {
          // Default: all types selected (mirrors the old behaviour where both booleans were true).
          this.selectedDecorationNames = this._allNamesSet();
        }
      },
      error: () => {
        // On error the set stays empty; the backward-compat getters below return true as a
        // safe fallback so the consumer continues to load products normally.
        this.decorationTypes = [];
        this.selectedDecorationNames = new Set<string>();
      }
    });
  }

  ngOnDestroy() {
    if (this._decorationSub) {
      this._decorationSub.unsubscribe();
    }
  }

  /** Returns a Set of all decoration names (lower-cased) from the current lookup. */
  private _allNamesSet(): Set<string> {
    return new Set<string>(this.decorationTypes.map(t => t.Name.toLowerCase()));
  }

  /** Persist the selected set to localStorage. */
  private _persistDecorationFilter(): void {
    localStorage.setItem(this.DECORATION_FILTER_KEY, JSON.stringify(Array.from(this.selectedDecorationNames)));
  }

  /** Toggle a decoration on/off, enforcing the at-least-one rule. */
  fn_ToggleDecorationType(name: string): void {
    const key = name.toLowerCase();
    if (this.selectedDecorationNames.has(key)) {
      // Enforce: at least one decoration must remain selected.
      if (this.selectedDecorationNames.size > 1) {
        this.selectedDecorationNames = new Set<string>(this.selectedDecorationNames);
        this.selectedDecorationNames.delete(key);
      }
    } else {
      this.selectedDecorationNames = new Set<string>(this.selectedDecorationNames);
      this.selectedDecorationNames.add(key);
    }
    this._persistDecorationFilter();
    this.updateProductList(false);
  }

  /** Whether a decoration name is currently selected. */
  fn_IsDecorationSelected(name: string): boolean {
    return this.selectedDecorationNames.has(name.toLowerCase());
  }

  // -----------------------------------------------------------------------
  // Backward-compat helpers: consumers still read screenPrint / embroidery
  // off the emitted event. We derive them from the generic selected set so
  // no consumer changes are required.
  // -----------------------------------------------------------------------

  /**
   * Returns true when "Digital Print" / "Digital" is in the selected set,
   * or when the set is empty (no types loaded — fail-open so products load).
   */
  private get _screenPrintSelected(): boolean {
    if (this.selectedDecorationNames.size === 0) return true;
    return this.selectedDecorationNames.has('digital print') ||
           this.selectedDecorationNames.has('digital');
  }

  /**
   * Returns true when "Embroidery" is in the selected set,
   * or when the set is empty (fail-open).
   */
  private get _embroiderySelected(): boolean {
    if (this.selectedDecorationNames.size === 0) return true;
    return this.selectedDecorationNames.has('embroidery');
  }



  updateProductList(reset: boolean) {
    // Emit backward-compatible screenPrint/embroidery keys for existing consumers
    // AND a generic decorations array for future consumers.
    this.getProducts.emit({
      reset: reset,
      screenPrint: this._screenPrintSelected,
      embroidery: this._embroiderySelected,
      decorations: Array.from(this.selectedDecorationNames)
    });
  }

  fn_EnableDisableResetFilterBtn() {
    // if (
    //   this.filterModel.Size.length > 0 ||
    //   this.filterModel.Brand.length > 0 ||
    //   this.filterModel.Color.length > 0 ||
    //   this.filterModel.Category !== ''
    // ) {
    //   this.isDisableFilterBtn = false;
    // } else {
    //   this.isDisableFilterBtn = true;
    // }
  }
  /** Function set selected category in filter model */
  fn_SelectCategory(id: string, isCategoryId: boolean) {

    this.flag = true;
    if (isCategoryId) {
      var parentCategory = this.filterList.Categories.find(x => x.ID.toString() == id).Name;
      this.showCategories = parentCategory;
    }
    else {
      let cat: any = {};
      this.filterList.Categories.forEach((c) => {
        if (c.SubCategories.some(x => x.ID.toString() == id)) {
          cat = {
            SubCat: c.SubCategories.find(x => x.ID.toString() == id),
            CatId: c.ID,
            CatName: c.Name
          };
        }
      });
      this.showCategories = cat.CatName + "/ " + cat.SubCat.Name;
      
    }
    localStorage.setItem("showCategories", this.showCategories);
    this.filterModel.Category = '';

    this.fn_UncheckFilterCheckboxes();
    this.filterModel.Category = id;
    this.fn_EnableDisableResetFilterBtn();
    this.filterModel.Skip = 0;
    this.filterModel.Take = this.pageSize;
    this.filterModel.searchBy = '';
    this.filterModel.Brand = [];
    this.filterModel.Color = [];
    this.filterModel.Size = [];
    this.updateProductList(false);
  }

  /** Modify filter model for selected brand */
  fn_SelectBrand(event: any, brandId: number, brandName: any) {
    this.flag = true;
    if (this.selectedBrand.indexOf(brandName) > -1) {
      const index = this.selectedBrand.indexOf(brandName, 0);
      if (index > -1) {
        this.selectedBrand.splice(index, 1);
      }
    } else
      this.selectedBrand.push(brandName)
    this.showBrand = this.selectedBrand.toString().replace(/,/g, ", ");
    localStorage.setItem("showBrand", this.showBrand);
    if (this.showBrand == "")
      this.flag = false;
    else
      this.flag = true;
    //else
    //  this.selectedMenu = this.selectedMenu + ", " + brandName;

    if (this.filterModel.Brand.length > 0) {
      for (let i = 0; i < this.filterModel.Brand.length; i++) {
        if (this.filterModel.Brand[i] === brandId && !event.checked) {
          this.filterModel.Brand.splice(i, 1);
        }
      }
    }
    if (event.checked) {
      this.filterModel.Brand.push(brandId);
    }
    this.filterModel.Skip = 0;
    this.filterModel.Take = this.pageSize;
    this.fn_EnableDisableResetFilterBtn();
    this.updateProductList(false);
  }

  /** Modify filter model for selected color */
  fn_SelectColor(event: any, colorName: string) {
    if (this.selectedColor.indexOf(colorName) > -1) {
      const index = this.selectedColor.indexOf(colorName, 0);
      if (index > -1) {
        this.selectedColor.splice(index, 1);
      }
    } else
      this.selectedColor.push(colorName)
    this.showColor = this.selectedColor.toString().replace(/,/g, ", ");
    localStorage.setItem("showColor", this.showColor);
    if (this.showColor == "")
      this.flag = false;
    else
      this.flag = true;
    if (this.filterModel.Color.length > 0) {
      for (let i = 0; i < this.filterModel.Color.length; i++) {
        if (this.filterModel.Color[i] === colorName && !event.checked) {
          this.filterModel.Color.splice(i, 1);
        }
      }
    }
    if (event.checked) {
      this.filterModel.Color.push(colorName);
    }
    this.filterModel.Skip = 0;
    this.filterModel.Take = this.pageSize;
    this.fn_EnableDisableResetFilterBtn();
    this.updateProductList(false);
  }

  /** Modify filter model for selected Size */
  fn_SelectSize(event: any, size: string) {
    if (this.selectedSize.indexOf(size) > -1) {
      const index = this.selectedSize.indexOf(size, 0);
      if (index > -1) {
        this.selectedSize.splice(index, 1);
      }
    } else
      this.selectedSize.push(size)
    this.showSize = this.selectedSize.toString().replace(/,/g, ", ");
    localStorage.setItem("showSize", this.showSize);
    if (this.showSize == "")
      this.flag = false;
    else
      this.flag = true;

    if (this.filterModel.Size.length > 0) {
      for (let i = 0; i < this.filterModel.Size.length; i++) {
        if (this.filterModel.Size[i] === size && !event.checked) {
          this.filterModel.Size.splice(i, 1);
        }
      }
    }
    if (event.checked) {
      this.filterModel.Size.push(size);
    }
    this.filterModel.Skip = 0;
    this.filterModel.Take = this.pageSize;
    this.fn_EnableDisableResetFilterBtn();
    this.updateProductList(false);
  }

  /** Modify filter model for selected print location ("Has <location> template") */
  fn_SelectPrintLocation(event: any, codeId: number, description: string) {
    if (this.selectedPrintLocation.indexOf(description) > -1) {
      const index = this.selectedPrintLocation.indexOf(description, 0);
      if (index > -1) {
        this.selectedPrintLocation.splice(index, 1);
      }
    } else
      this.selectedPrintLocation.push(description)
    this.showPrintLocation = this.selectedPrintLocation.toString().replace(/,/g, ", ");
    localStorage.setItem("showPrintLocation", this.showPrintLocation);
    if (this.showPrintLocation == "")
      this.flag = false;
    else
      this.flag = true;

    if (!this.filterModel.DecorationLocationCodeId) {
      this.filterModel.DecorationLocationCodeId = [];
    }
    if (this.filterModel.DecorationLocationCodeId.length > 0) {
      for (let i = 0; i < this.filterModel.DecorationLocationCodeId.length; i++) {
        if (this.filterModel.DecorationLocationCodeId[i] === codeId && !event.checked) {
          this.filterModel.DecorationLocationCodeId.splice(i, 1);
        }
      }
    }
    if (event.checked) {
      this.filterModel.DecorationLocationCodeId.push(codeId);
    }
    this.filterModel.Skip = 0;
    this.filterModel.Take = this.pageSize;
    this.fn_EnableDisableResetFilterBtn();
    this.updateProductList(false);
  }

  /**Clears selected filters */
  fn_ClearFilter() {

    this.flag = false;

    this.showBrand = "";
    this.showCategories = "";
    this.showSize = "";
    this.showColor = "";
    this.showPrintLocation = "";
    this.selectedBrand = [];
    this.selectedSize = [];
    this.selectedColor = [];
    this.selectedPrintLocation = [];

    this.filterModel.Skip = 0;
    this.filterModel.Take = this.pageSize;
    this.filterModel.Brand = [];
    this.filterModel.Color = [];
    this.filterModel.Size = [];
    this.filterModel.DecorationLocationCodeId = [];
    this.filterModel.Category = '';
    this.filterModel.searchBy = '';
    this.filterModel.ScreenPrint = true;
    this.filterModel.Embroidery = true;
    // Reset to all-selected (matches old default of both booleans = true).
    this.selectedDecorationNames = this._allNamesSet();
    this._persistDecorationFilter();
    this.searchCustomizedForm.patchValue({
      searchProduct: ''
    });

    this.localstorageService.set('masterproductfilter', JSON.stringify(this.filterModel));


    this.fn_EnableDisableResetFilterBtn();
    this.fn_UncheckFilterCheckboxes();
    this.updateProductList(true);
  }

  /** Function to uncheck all the filter(s) */
  fn_UncheckFilterCheckboxes() {
    this.brandCheckboxes.toArray().forEach((element: any) => {
      element.checked = false;
    });

    this.colorCheckboxes.toArray().forEach((element: any) => {
      element.checked = false;
    });

    this.sizeCheckboxes.toArray().forEach((element: any) => {
      element.checked = false;
    });

    this.printLocationCheckboxes.toArray().forEach((element: any) => {
      element.checked = false;
    });
  }

  fetchColor(hexCode: string | null | undefined): string {
    // Guard: Styles.HtmlColor1 is nullable in the DB and GetMasterProductFilters
    // can return ColorHexCode = null. Without this, the TypeError thrown here
    // aborts Angular change detection and blanks the whole master-products grid.
    if (!hexCode || !hexCode.trim()) {
      return '#CCCCCC';
    }
    const hex = hexCode.trim();
    return hex.includes('#') ? hex : '#' + hex;
  }

  fn_ShowReset(show: boolean) {
    this.flag = show;
  }

}


